import React from 'react';
import { Link } from 'react-router-dom';
import {
  Car, Store, Send, Globe, Zap, Users, Eye,
  CheckCircle, ChevronRight, Share2,
} from 'lucide-react';
import { LABELS, ONBOARDING_STEPS } from '../../constants/dashboard';

const VALUE_PROPS = [
  {
    icon: Zap,
    titleKey: 'heroValueSpeed',
    descKey: 'heroValueSpeedDesc',
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
  },
  {
    icon: Users,
    titleKey: 'heroValueNetwork',
    descKey: 'heroValueNetworkDesc',
    color: 'text-accent-purple',
    bg: 'bg-accent-purple/10',
  },
  {
    icon: Eye,
    titleKey: 'heroValueVisibility',
    descKey: 'heroValueVisibilityDesc',
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
  },
];

const PILLARS = [
  {
    icon: Car,
    titleKey: 'heroPillarStock',
    descKey: 'heroPillarStockDesc',
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/20',
  },
  {
    icon: Store,
    titleKey: 'heroPillarMarketplace',
    descKey: 'heroPillarMarketplaceDesc',
    color: 'text-accent-purple',
    bg: 'bg-accent-purple/10',
    border: 'border-accent-purple/20',
  },
  {
    icon: Send,
    titleKey: 'heroPillarSocial',
    descKey: 'heroPillarSocialDesc',
    color: 'text-green-400',
    bg: 'bg-green-500/10',
    border: 'border-green-500/20',
  },
  {
    icon: Globe,
    titleKey: 'heroPillarVitrine',
    descKey: 'heroPillarVitrineDesc',
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/20',
  },
];

const STEP_ICONS = { Share2, Car, Store, Globe };

const HeroSection = ({ firstName, completedSteps }) => {
  const pendingSteps = ONBOARDING_STEPS.filter(s => !completedSteps[s.id]);
  const totalSteps = ONBOARDING_STEPS.length;
  const doneCount = totalSteps - pendingSteps.length;
  const progressPct = Math.round((doneCount / totalSteps) * 100);

  return (
    <div className="bg-dark-card rounded-xl border border-dark-border overflow-hidden">
      {/* Hero header with gradient */}
      <div className="relative px-5 pt-6 pb-5 bg-gradient-to-br from-carmin/[0.08] via-dark-card to-accent-purple/[0.05]">
        <h2 className="text-xl font-bold text-primary mb-1">
          {LABELS.heroTitle.replace('{name}', firstName)}
        </h2>
        <p className="text-sm text-secondary">{LABELS.heroSubtitle}</p>

        {/* Value propositions */}
        <div className="flex flex-wrap gap-3 mt-5">
          {VALUE_PROPS.map(({ icon: Icon, titleKey, descKey, color, bg }) => (
            <div
              key={titleKey}
              className="flex items-center gap-2.5 px-3.5 py-2.5 bg-dark-bg/50 rounded-lg border border-dark-border"
            >
              <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${bg}`}>
                <Icon className={`h-4 w-4 ${color}`} />
              </div>
              <div>
                <p className={`text-sm font-semibold ${color}`}>{LABELS[titleKey]}</p>
                <p className="text-[11px] text-muted leading-tight">{LABELS[descKey]}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 4 Pillars */}
      <div className="px-5 py-4 border-t border-dark-border">
        <h3 className="text-xs font-semibold text-muted uppercase tracking-wider mb-3">{LABELS.heroPillarsTitle}</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
          {PILLARS.map(({ icon: Icon, titleKey, descKey, color, bg, border }) => (
            <div
              key={titleKey}
              className={`p-3 rounded-lg border ${border} bg-dark-hover/50`}
            >
              <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${bg} mb-2`}>
                <Icon className={`h-4 w-4 ${color}`} />
              </div>
              <p className="text-sm font-medium text-primary">{LABELS[titleKey]}</p>
              <p className="text-[11px] text-muted mt-0.5 leading-relaxed">{LABELS[descKey]}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Onboarding checklist */}
      <div className="px-5 py-4 border-t border-dark-border">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-primary">{LABELS.heroChecklistTitle}</h3>
          <span className="text-xs text-carmin font-medium">{doneCount}/{totalSteps}</span>
        </div>
        <p className="text-xs text-muted mb-3">{LABELS.heroChecklistSubtitle}</p>

        {/* Progress bar */}
        <div className="w-full h-1.5 bg-dark-hover rounded-full mb-3">
          <div
            className="h-full bg-carmin rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        <div className="space-y-2">
          {ONBOARDING_STEPS.map((step) => {
            const Icon = STEP_ICONS[step.icon];
            const isDone = completedSteps[step.id];

            return (
              <Link
                key={step.id}
                to={step.route}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                  isDone
                    ? 'bg-dark-hover border-green-900/30 opacity-60'
                    : 'bg-dark-hover border-dark-border hover:border-carmin/40'
                }`}
              >
                <div className={`flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 ${
                  isDone ? 'bg-green-500/10' : 'bg-carmin-50'
                }`}>
                  {isDone
                    ? <CheckCircle className="h-4 w-4 text-green-500" />
                    : <Icon className="h-4 w-4 text-carmin" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${isDone ? 'text-muted line-through' : 'text-primary'}`}>
                    {LABELS[step.labelKey]}
                  </p>
                  <p className="text-xs text-muted">{LABELS[step.descKey]}</p>
                </div>
                {!isDone && <ChevronRight className="h-4 w-4 text-muted flex-shrink-0" />}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
